import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../shared/lib/supabase'

export function useTechnologyStack(clientId) {
  const [technologies, setTechnologies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTechnologies = useCallback(async () => {
    if (!clientId) {
      setTechnologies([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('client_technology_stack')
        .select('*')
        .eq('client_id', clientId)
        .order('name', { ascending: true })

      if (fetchError) throw fetchError

      const transformed = (data || []).map(t => {
        // categories[] is the source of truth. Fall back to wrapping the
        // legacy single category text into an array if the new column is empty.
        let categories = Array.isArray(t.categories) ? t.categories : []
        if (categories.length === 0 && t.category) categories = [t.category]
        return {
          id: t.id,
          clientId: t.client_id,
          name: t.name,
          vendor: t.vendor,
          categories,
          // Keep `category` populated with the first entry for any code that
          // still reads the singular field (display fallbacks, exports).
          category: categories[0] || null,
          description: t.description,
          usageContext: t.usage_context,
          idealClientProfile: t.ideal_client_profile,
          notes: t.notes,
          assetClasses: t.asset_classes || [],
          operationalFunctions: t.operational_functions || [],
          createdAt: t.created_at,
          updatedAt: t.updated_at
        }
      })

      setTechnologies(transformed)
    } catch (err) {
      console.error('[useTechnologyStack] Fetch error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    fetchTechnologies()
  }, [fetchTechnologies])

  const addTechnology = async (techData) => {
    try {
      // Accept either categories[] (preferred) or singular category for
      // backward compat with any caller still using the old shape.
      const categories = Array.isArray(techData.categories)
        ? techData.categories.filter(Boolean)
        : (techData.category ? [techData.category] : [])

      const { data, error: insertError } = await supabase
        .from('client_technology_stack')
        .insert({
          client_id: clientId,
          name: techData.name,
          vendor: techData.vendor || null,
          // Keep singular `category` populated with the first entry for
          // legacy code paths until that column is fully retired.
          category: categories[0] || null,
          categories,
          description: techData.description || null,
          usage_context: techData.usageContext || null,
          ideal_client_profile: techData.idealClientProfile || null,
          notes: techData.notes || null,
          asset_classes: techData.assetClasses || [],
          operational_functions: techData.operationalFunctions || []
        })
        .select()
        .single()

      if (insertError) throw insertError

      const newTech = {
        id: data.id,
        clientId: data.client_id,
        name: data.name,
        vendor: data.vendor,
        categories: Array.isArray(data.categories) ? data.categories : (data.category ? [data.category] : []),
        category: data.category,
        description: data.description,
        usageContext: data.usage_context,
        idealClientProfile: data.ideal_client_profile,
        notes: data.notes,
        assetClasses: data.asset_classes || [],
        operationalFunctions: data.operational_functions || [],
        createdAt: data.created_at,
        updatedAt: data.updated_at
      }

      setTechnologies(prev =>
        [...prev, newTech].sort((a, b) => a.name.localeCompare(b.name))
      )
      return newTech
    } catch (err) {
      console.error('[useTechnologyStack] Add error:', err)
      setError(err.message)
      return null
    }
  }

  const updateTechnology = async (techId, updates) => {
    try {
      const dbUpdates = {}
      if (updates.name !== undefined) dbUpdates.name = updates.name
      if (updates.vendor !== undefined) dbUpdates.vendor = updates.vendor
      // Categories — accept array (preferred) or singular for back-compat
      if (updates.categories !== undefined) {
        const cats = Array.isArray(updates.categories) ? updates.categories.filter(Boolean) : []
        dbUpdates.categories = cats
        dbUpdates.category = cats[0] || null
      } else if (updates.category !== undefined) {
        dbUpdates.category = updates.category
        dbUpdates.categories = updates.category ? [updates.category] : []
      }
      if (updates.description !== undefined) dbUpdates.description = updates.description
      if (updates.usageContext !== undefined) dbUpdates.usage_context = updates.usageContext
      if (updates.idealClientProfile !== undefined) dbUpdates.ideal_client_profile = updates.idealClientProfile
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes
      if (updates.assetClasses !== undefined) dbUpdates.asset_classes = updates.assetClasses
      if (updates.operationalFunctions !== undefined)
        dbUpdates.operational_functions = updates.operationalFunctions

      const { error: updateError } = await supabase
        .from('client_technology_stack')
        .update(dbUpdates)
        .eq('id', techId)

      if (updateError) throw updateError

      setTechnologies(prev =>
        prev.map(t => t.id === techId ? { ...t, ...updates } : t)
          .sort((a, b) => a.name.localeCompare(b.name))
      )
      return true
    } catch (err) {
      console.error('[useTechnologyStack] Update error:', err)
      setError(err.message)
      return false
    }
  }

  const deleteTechnology = async (techId) => {
    try {
      const { error: deleteError } = await supabase
        .from('client_technology_stack')
        .delete()
        .eq('id', techId)

      if (deleteError) throw deleteError

      setTechnologies(prev => prev.filter(t => t.id !== techId))
      return true
    } catch (err) {
      console.error('[useTechnologyStack] Delete error:', err)
      setError(err.message)
      return false
    }
  }

  const buildCdmGrid = useCallback(() => {
    const grid = {}
    for (const tech of technologies) {
      for (const asset of tech.assetClasses) {
        for (const func of tech.operationalFunctions) {
          const key = `${asset}-${func}`
          if (!grid[key]) grid[key] = []
          grid[key].push(tech)
        }
      }
    }
    return grid
  }, [technologies])

  return {
    technologies,
    loading,
    error,
    addTechnology,
    updateTechnology,
    deleteTechnology,
    buildCdmGrid,
    refresh: fetchTechnologies
  }
}
